import styled, { css } from 'styled-components';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
  $fullWidth?: boolean;
}

const variantStyles = {
  primary: css`
    background: var(--button-primary-bg);
    color: var(--button-primary-text);
    border-color: transparent;

    &:hover:not(:disabled) {
      background: var(--button-primary-hover);
    }
  `,
  secondary: css`
    background: var(--bg-secondary);
    color: var(--gray-700);
    border-color: var(--gray-300);

    &:hover:not(:disabled) {
      background: var(--gray-100);
      color: var(--gray-800);
    }
  `,
  success: css`
    background: var(--success);
    color: white;
    border-color: transparent;

    &:hover:not(:disabled) {
      background: #256641;
    }
  `,
  danger: css`
    background: var(--error);
    color: white;
    border-color: transparent;

    &:hover:not(:disabled) {
      background: #8f1d18;
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--gray-600);
    border-color: transparent;

    &:hover:not(:disabled) {
      background: var(--gray-100);
      color: var(--gray-800);
    }
  `,
};

const sizeStyles = {
  sm: css`
    padding: var(--space-xs) var(--space-md);
    font-size: var(--font-size-xs);
  `,
  md: css`
    padding: var(--space-sm) var(--space-lg);
    font-size: var(--font-size-sm);
  `,
  lg: css`
    padding: var(--space-md) var(--space-xl);
    font-size: var(--font-size-base);
  `,
};

/**
 * Bottone standard dell'applicazione.
 *   <Button $variant="primary" onClick={...}>Salva</Button>
 */
export const Button = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  border: 1px solid transparent;
  border-radius: var(--border-radius-md);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background-color var(--transition-fast),
    border-color var(--transition-fast), color var(--transition-fast);
  text-decoration: none;
  white-space: nowrap;

  ${({ $variant = 'primary' }: ButtonProps) => variantStyles[$variant]}
  ${({ $size = 'md' }: ButtonProps) => sizeStyles[$size]}
  ${({ $fullWidth }: ButtonProps) => $fullWidth && 'width: 100%;'}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-500);
    outline-offset: 2px;
  }

  svg {
    width: 1em;
    height: 1em;
    flex-shrink: 0;
  }
`;

export default Button;
